import type { ReadingSlide } from "../ui/StartReader";
import { AssistantArt, EvidenceArt, GlossaryArt } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the-1700-release start screen. Three short
 * slides (what/who, main risks + why a human is the control, key definitions)
 * shown in the reader while the 3D room downloads. Content only; the reader UI
 * lives in ui/StartReader.tsx.
 */
export const THE_1700_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Ship fast with AI, without shipping holes.",
    illustration: AssistantArt,
    caption: "The assistant proposes. You decide.",
    sections: [
      {
        h: "What this is about",
        body: "A normal release day at a developer\u2019s desk. You build a customer feature with an AI coding assistant, review the code it writes, and decide whether the build ships \u2014 without leaking data or shipping a vulnerability.",
      },
      {
        h: "Who it\u2019s for",
        body: "Developers, and anyone who uses an AI coding assistant, reviews code, or signs off a release. No specific language or framework needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Tools and pipelines catch a lot, but not everything. A test only proves what it checks. A scanner only flags what it already knows. An AI agent does whatever the context tells it \u2014 including text an attacker planted. Whether a change is actually safe to ship is a human call. Yours.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "Where a fast release goes wrong.",
    illustration: EvidenceArt,
    caption: "A green check is evidence for one thing only.",
    sections: [
      {
        h: "What you feed the AI leaves your control",
        body: "Real customer data or a secret sent to an assistant is a disclosure, even in an approved tool. No scanner can un-send it \u2014 so you decide what goes in.",
      },
      {
        h: "The agent obeys hidden instructions",
        body: "Text in an issue or a web page can carry commands the agent follows (prompt injection). Only a human reviewing each action it takes will catch it.",
      },
      {
        h: "Green doesn\u2019t mean safe",
        body: "A passing test proves only the assertion that ran. If a change quietly weakens the check, CI still goes green. You judge whether the evidence really covers the risk.",
      },
      {
        h: "Secrets and access leak silently",
        body: "An exposed token or a missing owner-check throws no error. Someone has to notice, contain it, and get it revoked.",
      },
    ],
  },
  {
    tab: "Key concepts",
    headline: "The words you\u2019ll meet today.",
    illustration: GlossaryArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "AI coding agent", body: "A tool that reads your code and context and writes or changes code for you. It acts on whatever you give it." },
      { h: "Context", body: "Everything you hand the assistant \u2014 files, requirements, logs. Whatever is in it, the provider processes and other tools can read." },
      { h: "Prompt injection", body: "Instructions hidden in data the agent reads (an issue, a page) that try to make it act against you." },
      { h: "Broken access control (IDOR)", body: "The server returns a record just because you asked for its id, without checking it\u2019s yours. Change the id, read someone else\u2019s data." },
      { h: "Injection \u2014 SQL & XSS", body: "Untrusted text run as a command: in the database (SQL injection) or in a victim\u2019s browser (cross-site scripting)." },
      { h: "Supply chain", body: "Code you didn\u2019t write that runs on your machine or in your build \u2014 a package, its install hook, a dependency." },
      { h: "Secret", body: "A credential such as a token or key. Hiding it doesn\u2019t make it safe; it must be revoked and rotated." },
    ],
  },
];
